import { useDebounce } from 'use-debounce';

import { type FocusAskImpactedWorkData } from '../../types';

import { isValidImpactedWorkUrl } from './is-valid-impacted-work-url';
import { useResolveImpactedWorkInput } from './useResolveImpactedWorkInput';
import { useValidateImpactedWorkAri } from './useValidateImpactedWorkAri';

export type ImpactedWorkResolutionState =
	| { status: 'idle' }
	| { status: 'invalidUrl' }
	| { status: 'resolving' }
	| { status: 'notFound' }
	| { status: 'resolved'; data: FocusAskImpactedWorkData };

export type ImpactedWorkResolutionStatus = ImpactedWorkResolutionState['status'];

const DEBOUNCE_MS = 350;

/**
 * Takes the raw text input value and returns a single discriminated-union
 * state that the UI can switch on without juggling multiple booleans.
 *
 * Behavior:
 *   - Empty input                    → 'idle'                (synchronous, no debounce)
 *   - Invalid URL                    → 'invalidUrl'          (synchronous, no debounce)
 *   - User still typing              → 'resolving'           (debounce hasn't caught up yet)
 *   - In-flight resolver request     → 'resolving'           (network round-trip)
 *   - Resolver returned any error     → 'notFound'
 *   - Resolved but not accessible (wrong org, unconnected site, unsupported type) → 'notFound'
 *   - Resolved successfully          → 'resolved' with `data`
 *
 * When initialValue has an ARI but no link (e.g. Rovo pre-fill), the ARI path
 * is used to fetch the URL. Once the user types anything, the link path takes over.
 */
export const useImpactedWorkResolution = (
	input: string,
	cloudId: string,
	initialValue?: FocusAskImpactedWorkData | null,
): ImpactedWorkResolutionState => {
	const trimmed = input.trim();
	const [debouncedInput] = useDebounce(trimmed, DEBOUNCE_MS);

	// When input is empty and initialValue has only an ARI, use it to resolve the URL.
	// The moment the user types anything, debouncedInput takes over as the link.
	const resolveAri = trimmed.length === 0 ? initialValue?.id : null;
	const resolveLink = debouncedInput || initialValue?.link || null;

	const { ari, link, isFetching, error } = useResolveImpactedWorkInput({
		ari: resolveAri,
		link: resolveLink,
	});

	// Validates ARI for correct type, org, site connected, etc.
	const ariValidation = useValidateImpactedWorkAri(ari, cloudId, link);

	// Empty/invalid input is decided without waiting for the debounce, so the UI reacts instantly.
	if (trimmed.length === 0 && !initialValue?.id) return { status: 'idle' };
	if (trimmed.length > 0 && !isValidImpactedWorkUrl(trimmed)) return { status: 'invalidUrl' };

	// User has typed valid input but the debounce hasn't caught up yet —
	// render the 'resolving' state so we don't briefly flash a stale error.
	if (debouncedInput !== trimmed) return { status: 'resolving' };
	if (isFetching) return { status: 'resolving' };

	if (error) {
		return { status: 'notFound' }; // Will surface not found message for errors.
	}

	// Done fetching with no explicit API error, but resolver could not resolve to an ARI.
	if (!ari || !link) return { status: 'notFound' };

	// ARI scope validation — handles both type validation (unsupported) and scope validation (org/site checks).
	if (ariValidation.status === 'loading') return { status: 'resolving' };
	if (ariValidation.status === 'notFound' || ariValidation.status === 'error')
		return { status: 'notFound' };

	return {
		status: 'resolved',
		data: { id: ari, link },
	};
};
